import React from "react"
import PropTypes from "prop-types" 
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import appletouchicon from "../images/favicons/apple-touch-icon.png";
import faviconsvg from "../images/favicons/favicon.svg";
import faviconico from "../images/favicons/favicon.ico";
import maskicon from "../images/favicons/mask-icon.svg";

function SEO({title, description, keywords, articleBody, lang, meta, metaImage, pathname,isAccessibleForFree,datePublished,dateModified}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            keywords
            siteUrl
            social {
              twitter
              facebookAppID
            }
          }
        }
      }
    `
  )
  
  const faviconLinks = [
    { rel: "icon", href: faviconsvg },
    { rel: "icon", href: faviconico },
    { rel: "mask-icon", href: maskicon, color: "#1e87f0"},
    { rel: "apple-touch-icon", href: appletouchicon },
  ];  

  const metaDescription = description || site.siteMetadata.description
  const twitterStem = "https://www.twitter.com/"
  const twitterHandle = site.siteMetadata.social.twitter
  const fbID = site.siteMetadata.social.facebookAppID
  const fullTwitterHandle = twitterStem.concat(twitterHandle)
  const metaKeywords = 
    keywords.length > 0
      ? site.siteMetadata.keywords.concat(',' + keywords)
      : site.siteMetadata.keywords
  const image =
    metaImage && metaImage.src
      ? metaImage.src
      : null
  const canonical = pathname ? `${site.siteMetadata.siteUrl}${pathname}` : null

  const websiteJSONLD =  {
    "@context":"http://schema.org",
    "@type":"WebSite",
    "name":site.siteMetadata.author,
    "url": site.siteMetadata.siteUrl,
  }

  const articleJSONLD = 
    datePublished ? 
      JSON.stringify(
        {
          "@context":"http://schema.org",
          "@type":"NewsArticle",
          "headline":title,
          "datePublished":datePublished,
          "dateModified":dateModified,
          "description":description,
          "isAccessibleForFree":isAccessibleForFree,
          "keywords": metaKeywords,
          "isPartOf":{
              "@type":"CreativeWork",
              "name":site.siteMetadata.author,
          },
          "alternativeHeadline":title,
          "articleBody":articleBody,
          "image":{
              "@type":"ImageObject",
              "@context":"http://schema.org",
              "url":image,
              "width":metaImage.width,
              "height":metaImage.height
          },
          "url":canonical,
          "mainEntityOfPage":{
              "@type":"WebPage",
              "@id":canonical
          },
          "publisher":{
              "@type":"Organization",
              "@context":"http://schema.org",
              "name":site.siteMetadata.author,
              "logo":{
                  "@type":"ImageObject",
                  "@context":"http://schema.org",
                  "url":"https://images.prismic.io/phipost/963561ad-0127-4d97-aa28-e34573cb3bdd_Φpost_small.png?auto=compress,format",
                  "width":894,
                  "height":418
              },
              "url":site.siteMetadata.siteUrl,
              "sameAs":fullTwitterHandle
          }
        }
      ) 
    : null
    
  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={
        [
          canonical ? {
            rel: "canonical",
            href: canonical,
          } : {},
          {faviconLinks}
        ]
      }

      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: "keywords",
          content: metaKeywords,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:url`,
          content: canonical,
        },
        {
          property: `og:type`,
          content: `article`,
        },
        {
          property: `fb:app_id`,
          content: fbID,
        },
        {
          property: `og:locale`,
          content: `en_US`,
        },
        {
          name: `twitter:creator`,
          content: twitterHandle,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
      ]
        .concat(
          metaImage ? [
                {
                  property: "og:image",
                  content: image,
                },
                {
                  property: "og:image:width",
                  content: metaImage.width,
                },
                {
                  property: "og:image:height",
                  content: metaImage.height,
                },
                {
                  name: "twitter:card",
                  content: "summary_large_image",
                },
              ] : [
                {
                  name: "twitter:card",
                  content: "summary",
                },
              ]
        )
        .concat(meta)}
    >
      <script type="application/ld+json">
        {JSON.stringify(websiteJSONLD)}
      </script> 
      
      {articleJSONLD} 
      ?   <script type="application/ld+json">
           {articleJSONLD}
          </script> 
      : null

    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
  keywords: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
  }),
  pathname: PropTypes.string,
}

export default SEO